import {
  numberClusterConfigFallback,
  sortedNumberClusterEntries,
  sortedTimeClusterEntries,
  timeClusterConfigFallback,
} from './constants';
import {
  getDataRangeInMilliseconds,
  getNumberRange,
} from '../components/_utils';
import { ChartCluster } from '../types';

type DetermineClusterConfig = { dataRange: number; isTime: boolean };

export const determineClusterConfig = ({
  dataRange,
  isTime,
}: DetermineClusterConfig): number => {
  const sortedEntries = isTime
    ? sortedTimeClusterEntries
    : sortedNumberClusterEntries;

  for (const { range, value } of sortedEntries) {
    if (dataRange <= range) {
      return value;
    }
  }

  return sortedEntries.at(-1)?.value ?? isTime
    ? timeClusterConfigFallback
    : numberClusterConfigFallback;
};

type GroupIntoClustersParams<T> = {
  chartData: T[];
  dataKey: keyof T;
  groupByKey?: keyof T;
  clusterRange: number;
  min: number;
};

export const groupIntoClusters = <T>({
  chartData,
  dataKey,
  groupByKey,
  clusterRange,
  min,
}: GroupIntoClustersParams<T>): ChartCluster<T>[] => {
  if (chartData.length === 0) {
    return [];
  }

  const bucketMap = new Map<string, ChartCluster<T>>();

  for (const item of chartData) {
    const value = item[dataKey] as number;
    const bucketIndex = Math.floor((value - min) / clusterRange);
    const bucketStart = min + bucketIndex * clusterRange;
    const bucketEnd = bucketStart + clusterRange;

    if (groupByKey) {
      const groupValue = String(item[groupByKey]);
      const key = `${bucketIndex}_${groupValue}`;

      if (!bucketMap.has(key)) {
        bucketMap.set(key, {
          start: bucketStart,
          end: bucketEnd,
          [groupValue]: 0,
          [`${groupValue}_entries`]: [],
        });
      }

      const bucket = bucketMap.get(key)!;
      bucket[groupValue] = (bucket[groupValue] as number) + 1;
      (bucket[`${groupValue}_entries`] as T[]).push(item);
    } else {
      const key = `${bucketIndex}`;
      if (!bucketMap.has(key)) {
        bucketMap.set(key, {
          start: bucketStart,
          end: bucketEnd,
          count: 0,
          entries: [],
        });
      }

      const bucket = bucketMap.get(key)!;
      bucket.count = (bucket.count ?? 0) + 1;
      bucket.entries!.push(item);
    }
  }

  return Array.from(bucketMap.values());
};

export const assignClusterPoints = <T>(
  clusters: ChartCluster<T>[],
  dataKey: keyof T,
  groupByKey?: keyof T
): void => {
  for (const cluster of clusters) {
    let entries: T[];

    if (groupByKey) {
      const groupVal = Object.keys(cluster).find((k) =>
        k.endsWith('_entries')
      )!;
      entries = cluster[groupVal] as T[];
    } else {
      entries = cluster.entries ?? [];
    }

    if (entries.length === 0) {
      cluster.clusterPoint = (cluster.start + cluster.end) / 2;
      continue;
    }

    const middleIndex = Math.floor(entries.length / 2);
    cluster.clusterPoint = entries[middleIndex][dataKey] as number;
  }
};

type ClusterDataParams<T> = {
  chartData: T[];
  dataKey: keyof T;
  groupByKey?: keyof T;
  isTime?: boolean;
};

export const clusterData = <T>({
  chartData,
  dataKey,
  groupByKey,
  isTime = false,
}: ClusterDataParams<T>): ChartCluster<T>[] => {
  const { min, range } = isTime
    ? getDataRangeInMilliseconds(chartData, dataKey)
    : getNumberRange(chartData, dataKey);

  const clusterRange = determineClusterConfig({
    dataRange: range,
    isTime,
  });

  const clusteredData = groupIntoClusters({
    chartData,
    dataKey,
    clusterRange,
    min,
    groupByKey,
  });

  assignClusterPoints(clusteredData, dataKey, groupByKey);

  return clusteredData.sort((a, b) => a.clusterPoint! - b.clusterPoint!);
};
